import React, {
  PropsWithChildren,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { twMerge } from "tailwind-merge";
// Classes applied to the row the user last clicked before navigating away, so it
// is highlighted when they return. Mirrors the Punch List pattern.
import { selectedRowClassName } from "../../Constants/Consts";

export default function List<T>({
  name,
  data,
  onClick,
  isItemDisabled: isDisabled,
  isItemSelected,
  renderChildItem,
  rowHoverClassName = "hover:bg-gray-100 hover:cursor-pointer",
  virtualize = false,
  estimatedItemHeight = 72,
  scrollToSelectedOnMount = false,
  }: {
  data: Array<T> | undefined;
  name: string;
  onClick: (item: T) => void;
  isItemDisabled: boolean | ((item: T) => boolean);
  isItemSelected?: (item: T) => boolean;
  rowHoverClassName?: string;
  virtualize?: boolean;
  estimatedItemHeight?: number;
  scrollToSelectedOnMount?: boolean;
  renderChildItem: (
    item: T
  ) => React.FunctionComponent<
    PropsWithChildren<{ onClick?: (item: T) => void, className: string }>
  >;
}) {
  // useWindowVirtualizer measures scroll against the document, so it must know
  // how far the list sits below the top of the page (breadcrumb, search bar,
  // filters). Without this scrollMargin the visible-row math is offset by the
  // header height and scrollToIndex lands in the wrong place on back-navigation.
  const listContainerRef = useRef<HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = useState(0);
  useLayoutEffect(() => {
    if (!virtualize) return;
    const el = listContainerRef.current;
    if (el) setScrollMargin(el.offsetTop);
  }, [virtualize, data]);

  const rowVirtualizer = useWindowVirtualizer({
    count: virtualize ? (data?.length ?? 0) : 0,
    estimateSize: () => estimatedItemHeight,
    overscan: 5,
    scrollMargin,
  });

  const didScrollToSelected = useRef(false);
  const latestDataRef = useRef(data);
  latestDataRef.current = data;
  const latestIsSelectedRef = useRef(isItemSelected);
  latestIsSelectedRef.current = isItemSelected;
  useEffect(() => {
    if (!virtualize || !scrollToSelectedOnMount || !isItemSelected) return;
    if (didScrollToSelected.current || !data?.length) return;
    // Wait until the selected row is actually present (the parent may populate or
    // re-sort the list a tick after mount). One findIndex serves as both the
    // presence gate and the target, avoiding a separate full pass over data.
    if (data.findIndex((it) => isItemSelected(it)) < 0) return;
    didScrollToSelected.current = true;
    let attempts = 0;
    let rafId = 0;
    const scroll = () => {
      const items = latestDataRef.current;
      const isSelected = latestIsSelectedRef.current;
      if (items && isSelected) {
        const idx = items.findIndex((it) => isSelected(it));
        if (idx >= 0) rowVirtualizer.scrollToIndex(idx, { align: "center" });
      }
      attempts += 1;
      if (attempts < 5) rafId = requestAnimationFrame(scroll);
    };
    rafId = requestAnimationFrame(scroll);
    return () => cancelAnimationFrame(rafId);
  }, [data, virtualize, scrollToSelectedOnMount, isItemSelected, rowVirtualizer]);

  if (virtualize) {
    return (
      <div
        ref={listContainerRef}
        data-testid={`${name}-list-table`}
        className="min-w-full"
      >
        <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, width: "100%", position: "relative" }}>
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const item = data![virtualRow.index];
            const disabled = typeof isDisabled === "function" ? isDisabled(item) : isDisabled;
            const selected = !disabled && isItemSelected?.(item);
            const ReactItem = renderChildItem(item);
            return (
              <div
                key={virtualRow.key}
                data-index={virtualRow.index}
                ref={rowVirtualizer.measureElement}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  transform: `translateY(${virtualRow.start - rowVirtualizer.options.scrollMargin}px)`,
                }}
                className={twMerge(
                  "border-b border-gray-200",
                  disabled
                    ? "bg-zinc-50 text-opacity-50 cursor-not-allowed"
                    : rowHoverClassName,
                  selected ? selectedRowClassName : ""
                )}
                onClick={() => {if (!disabled) onClick?.(item);}}
              >
                <div className="py-4 text-left text-sm text-gray-900 dark:text-white">
                  <ReactItem className="" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <table className="min-w-full divide-y divide-gray-200" data-testid={`${name}-list-table`}>
      <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-1000">
        {data?.map((item, i) => {
          const disabled =
            typeof isDisabled === "function" ? isDisabled?.(item) : isDisabled;
          const selected = !disabled && isItemSelected?.(item);
          const ReactItem = renderChildItem(item);

          return (
            <tr
              key={i}
              onClick={() => {
                if (!disabled) onClick?.(item);
              }}
              className={twMerge(
                "group",
                disabled
                  ? "bg-zinc-50 text-opacity-50 cursor-not-allowed"
                  : rowHoverClassName,
                selected ? selectedRowClassName : ""
              )}
            >
              <td className="py-4 whitespace-nowrap text-left text-sm text-gray-900 dark:text-white">
                <ReactItem className={""} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
